/* eslint-disable @typescript-eslint/naming-convention */
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ExecutionResult, LcsApiService } from '@lcs/shared/api';
import { PromptInput, PromptTypes } from 'generated/graphql-types';
import { Observable } from 'rxjs';
import { CurrentUser } from 'src/app/authentication/models/current-user.interface';
import { PromptDetailSaveModel } from '../../prompt-details/models/prompt-detail-save.model';
import { SavePrompt, SavePromptVariables } from '../../prompt-details/queries/generated/SavePrompt';
import { SavePromptMutation } from '../../prompt-details/queries/save-prompt.graphql';
import { FileSaveModel } from '../models/save-file-only-models';
import { SaveFile, SaveFileVariables } from '../queries/generated/SaveFile';
import { saveFileMutation } from '../queries/SaveFile.graphql';

@Injectable({ providedIn: 'root' })
export class AddPromptApiService {
   constructor(private apiService: LcsApiService, private http: HttpClient) {}

   create(
      promptDetail: PromptDetailSaveModel,
      file: File | null,
      promptIdIfUpdatingExistingPrompt: number | null
   ): Observable<ExecutionResult<SavePrompt>> {
      let fields: (keyof PromptInput)[];

      if (promptIdIfUpdatingExistingPrompt != null) {
         fields = ['isActive', 'name', 'accountId', 'typeId', 'fileId', 'isReusable', 'promptId'];

         promptDetail.promptId = promptIdIfUpdatingExistingPrompt;
      } else {
         fields = ['isActive', 'name', 'accountId', 'typeId', 'fileId', 'isReusable'];
      }

      if (file) {
         const body: { query: string; variables: SavePromptVariables } = {
            query: SavePromptMutation,
            variables: {
               prompt: {
                  ...promptDetail,
                  fields: [...fields, 'file.accountId', 'file.metaTag', 'file.promptType', 'file.filename'],
               },
            },
         };
         const formData = new FormData();
         formData.append('request', JSON.stringify(body));
         formData.append(file.name, file);

         return this.http.post<ExecutionResult<SavePrompt>>(this.apiService.graphApiUrl, formData);
      } else {
         return this.apiService.query<SavePrompt, SavePromptVariables>({
            query: SavePromptMutation,
            variables: {
               prompt: {
                  ...promptDetail,
                  fields: fields,
               },
            },
         });
      }
   }

   createFileOnly(fileModel: FileSaveModel): Observable<ExecutionResult<SaveFile>> {
      const body: { query: string; variables: SaveFileVariables } = {
         query: saveFileMutation,
         variables: {
            file: {
               accountId: fileModel.fileDetail?.accountId,
               fileName: fileModel.fileDetail?.fileName,
               fields: fileModel.fields,
            },
         },
      };
      let formData = new FormData();
      formData.append('request', JSON.stringify(body));
      if (fileModel.file) {
         formData.append(fileModel.file.name, fileModel.file);
      }

      return this.http.post<ExecutionResult<SaveFile>>(this.apiService.graphApiUrl, formData);
   }

   callExtensionToRecordPrompt(
      promptDetail: PromptDetailSaveModel,
      currentUser: CurrentUser | null,
      routeToExtension: string,
      destinationNumber: string,
      sessionId: string
   ): Observable<{ PromptRecordingCallDetailId: number }> {
      const userId = currentUser ? currentUser.userId : null;
      const body = {
         AccountId: promptDetail.accountId,
         DestinationExtension: routeToExtension,
         DestinationNumber: destinationNumber,
         IsActive: promptDetail.isActive,
         IsReusable: promptDetail.isReusable,
         Name: promptDetail.name,
         SessionId: sessionId,
         Type: promptDetail.typeId,
         PromptId: promptDetail.promptId,
         UserId: userId,
      };
      const httpOptions = {
         headers: new HttpHeaders({
            'Content-Type': 'application/json',
         }),
      };
      return this.http.post<{ PromptRecordingCallDetailId: number }>(
         this.apiService.buildApiUrl('call/recordPrompt'),
         JSON.stringify(body),
         httpOptions
      );
   }

   callToUpdateExistingPromptFile(
      accountId: number,
      promptId: number,
      promptName: string,
      promptType: PromptTypes,
      currentUser: CurrentUser | null,
      routeToExtension: string,
      destinationNumber: string,
      sessionId: string
   ): Observable<{ PromptRecordingCallDetailId: number }> {
      const userId = currentUser ? currentUser.userId : null;
      const body = {
         AccountId: accountId,
         PromptId: promptId,
         DestinationExtension: routeToExtension,
         DestinationNumber: destinationNumber,
         SessionId: sessionId,
         UserId: userId,
         Type: promptType,
         Name: promptName,
      };
      const httpOptions = {
         headers: new HttpHeaders({
            'Content-Type': 'application/json',
         }),
      };
      return this.http.post<{ PromptRecordingCallDetailId: number }>(
         this.apiService.buildApiUrl('call/recordPrompt'),
         JSON.stringify(body),
         httpOptions
      );
   }

   hangupActiveCall(serverIp: string | null, channelId: string | null, callDetailId: any | null): void {
      const httpOptions = {
         headers: new HttpHeaders({
            'Content-Type': 'application/json',
         }),
      };
      if ((serverIp && serverIp !== '' && channelId && channelId !== '') || callDetailId) {
         this.http
            .post(
               this.apiService.buildApiUrl('call/hangupcall'),
               JSON.stringify({
                  channelId: channelId ?? '',
                  serverIp: serverIp ?? '',
                  callDetailId: callDetailId,
                  isPromptRecordHangup: true,
               }),
               httpOptions
            )
            .subscribe();
      }
   }
}
